import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { PersonalShadowAiStats } from '@kirin/api-client/contracts/account-shadow-ai-stats';
import {
  listOrganizationShadowAiTools,
  listUserShadowAiTools,
  listOrganizationRunningProcesses,
  listUserRunningProcesses,
  getShadowAiToolUsers,
  getOrgShadowAiStats,
  getPersonalShadowAiStats,
  listOrgNetworkConnections,
  listOrgNetworkDomains,
  getNetworkConnectionUsers,
  getOrgNetworkStats,
  getAgentNames,
  getOrgTopShadowAiUsers,
  getOrgTopShadowAiTools,
  type ListShadowAiToolsParams,
  type ListRunningProcessesParams,
  type ListNetworkConnectionsParams,
} from '@kirin/api-client/api/shadow-ai';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/hooks/useAuth';
import { queryKeys } from './queryKeys';
import { cacheConfigForCollaborativeData } from './queryConfig';
import { retryHandlers } from './helpers';

export const SHADOW_AI_REFRESH_INTERVAL = 30_000;
export const RUNNING_PROCESSES_REFRESH_INTERVAL = 5_000;

export function useUserInstalledTools(
  organizationId: string | undefined,
  userId: string | null,
  enabled: boolean = true
) {
  return useQuery<
    Array<{
      id: string;
      name: string;
      category: string;
      lastSeenAt: string;
    }>,
    Error
  >({
    queryKey: queryKeys.shadowAi.userInstalledTools(organizationId, userId),
    queryFn: async () => {
      if (!organizationId || !userId) {
        throw new Error('organizationId and userId are required');
      }
      const response = await listOrganizationShadowAiTools(
        apiClient,
        organizationId,
        {
          userId,
          sourceType: 'installed',
          limit: 100,
        }
      );
      return response.tools.map(tool => ({
        id: tool.toolId,
        name: tool.name,
        category: tool.category,
        lastSeenAt: tool.lastUsed ?? tool.addedAt ?? new Date().toISOString(),
      }));
    },
    enabled: enabled && !!organizationId && !!userId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

export function useUserBrowserTools(
  organizationId: string | undefined,
  userId: string | null,
  enabled: boolean = true
) {
  return useQuery<
    Array<{
      id: string;
      domain: string;
      name: string;
      category: string;
      lastSeenAt: string;
    }>,
    Error
  >({
    queryKey: queryKeys.shadowAi.userBrowserTools(organizationId, userId),
    queryFn: async () => {
      if (!organizationId || !userId) {
        throw new Error('organizationId and userId are required');
      }
      const response = await listOrganizationShadowAiTools(
        apiClient,
        organizationId,
        {
          userId,
          sourceType: 'browser',
          limit: 100,
        }
      );
      return response.tools.map(tool => ({
        id: tool.toolId,
        domain: tool.toolId, // For browser tools, toolId contains the actual domain (e.g., "chat.openai.com")
        name: tool.name,
        category: tool.category,
        lastSeenAt: tool.lastUsed ?? tool.addedAt ?? new Date().toISOString(),
      }));
    },
    enabled: enabled && !!organizationId && !!userId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

export function useShadowAiTools(
  organizationId: string | undefined,
  params: ListShadowAiToolsParams = {},
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.shadowAi.list(organizationId, params),
    queryFn: () => {
      if (!organizationId) {
        throw new Error('organizationId is required');
      }
      return listOrganizationShadowAiTools(apiClient, organizationId, params);
    },
    enabled: enabled && !!organizationId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
    placeholderData: prev => prev,
  });
}

export function useUserShadowAiTools(
  params: ListShadowAiToolsParams = {},
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.shadowAi.personal(params),
    queryFn: () => {
      return listUserShadowAiTools(apiClient, params);
    },
    enabled,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
    refetchInterval: SHADOW_AI_REFRESH_INTERVAL,
    placeholderData: prev => prev,
  });
}

export function useRunningProcesses(
  organizationId: string | undefined,
  params: ListRunningProcessesParams = {},
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.shadowAi.running(organizationId, params),
    queryFn: () => {
      if (!organizationId) {
        throw new Error('organizationId is required');
      }
      return listOrganizationRunningProcesses(
        apiClient,
        organizationId,
        params
      );
    },
    enabled: enabled && !!organizationId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
    refetchInterval: RUNNING_PROCESSES_REFRESH_INTERVAL,
  });
}

export function useUserRunningProcesses(
  params: ListRunningProcessesParams = {},
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.shadowAi.runningPersonal(params),
    queryFn: () => {
      return listUserRunningProcesses(apiClient, params);
    },
    enabled,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
    refetchInterval: RUNNING_PROCESSES_REFRESH_INTERVAL,
    placeholderData: prev => prev,
  });
}

export function useToolUsers(
  organizationId: string | undefined,
  toolId: string | undefined,
  enabled: boolean = true,
  /**
   * KIR-3495: narrow the expanded row's user list to one org member, so the
   * Shadow AI user-scope page does not list the whole org under a tool.
   */
  scopeUserId?: string
) {
  return useQuery({
    queryKey: scopeUserId
      ? [
          ...queryKeys.shadowAi.toolUsers(organizationId, toolId),
          { scopeUserId },
        ]
      : queryKeys.shadowAi.toolUsers(organizationId, toolId),
    queryFn: () => {
      if (!organizationId || !toolId) {
        throw new Error('organizationId and toolId are required');
      }
      return getShadowAiToolUsers(
        apiClient,
        organizationId,
        toolId,
        scopeUserId
      );
    },
    enabled: enabled && !!organizationId && !!toolId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

export function useShadowAiOrgStats(
  organizationId: string | undefined,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.shadowAi.stats(organizationId),
    queryFn: () => {
      if (!organizationId) {
        throw new Error('organizationId is required');
      }
      return getOrgShadowAiStats(apiClient, organizationId);
    },
    enabled: enabled && !!organizationId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
    refetchInterval: SHADOW_AI_REFRESH_INTERVAL,
  });
}

// Gate lives in the component; the hook only forwards `enabled` to useQuery.
export function useUserShadowAiStats(
  enabled: boolean = true
): UseQueryResult<PersonalShadowAiStats, unknown> {
  // Key on identity so a switched account never reads the prior user's cache.
  const { user } = useAuth();
  return useQuery({
    queryKey: queryKeys.shadowAi.personalStats(user?.email),
    queryFn: () => getPersonalShadowAiStats(apiClient),
    enabled,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
    refetchInterval: SHADOW_AI_REFRESH_INTERVAL,
  });
}

export function useOrgNetworkConnections(
  organizationId: string | undefined,
  params: ListNetworkConnectionsParams = {},
  enabled: boolean = true
) {
  // Exclude knownTotal and countScope from the query key — both are
  // request-shape perf hints (they tell the backend whether to skip COUNT)
  // and would otherwise spawn a new cache entry on every render where the
  // hint changes, defeating the optimization.
  const { knownTotal, countScope, ...queryKeyParams } = params;
  void knownTotal;
  void countScope;
  return useQuery({
    queryKey: queryKeys.shadowAi.networkConnections(
      organizationId,
      queryKeyParams
    ),
    queryFn: () => {
      if (!organizationId) {
        throw new Error('organizationId is required');
      }
      return listOrgNetworkConnections(apiClient, organizationId, params);
    },
    enabled: enabled && !!organizationId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
    refetchInterval: SHADOW_AI_REFRESH_INTERVAL,
    // Pause the 30s poll when the tab is in the background — the data is
    // not visible and the join-heavy network endpoints are the most
    // expensive in this view.
    refetchIntervalInBackground: false,
    placeholderData: prev => prev,
  });
}

export function useNetworkConnectionUsers(
  organizationId: string | undefined,
  domain: string | null,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.shadowAi.networkConnectionUsers(organizationId, domain),
    queryFn: () => {
      if (!organizationId || !domain) {
        throw new Error('organizationId and domain are required');
      }
      return getNetworkConnectionUsers(apiClient, organizationId, domain);
    },
    enabled: enabled && !!organizationId && !!domain,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

export function useOrgNetworkStats(
  organizationId: string | undefined,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.shadowAi.networkStats(organizationId),
    queryFn: () => {
      if (!organizationId) {
        throw new Error('organizationId is required');
      }
      return getOrgNetworkStats(apiClient, organizationId);
    },
    enabled: enabled && !!organizationId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
    refetchInterval: SHADOW_AI_REFRESH_INTERVAL,
    refetchIntervalInBackground: false,
  });
}

export function useAgentNames(
  organizationId: string | undefined,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ['shadowAi', 'agentNames', organizationId],
    queryFn: () => {
      if (!organizationId) {
        throw new Error('organizationId is required');
      }
      return getAgentNames(apiClient, organizationId);
    },
    enabled: enabled && !!organizationId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

export function useOrgNetworkDomains(
  organizationId: string | undefined,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.shadowAi.networkDomains(organizationId),
    queryFn: () => {
      if (!organizationId) {
        throw new Error('organizationId is required');
      }
      return listOrgNetworkDomains(apiClient, organizationId);
    },
    enabled: enabled && !!organizationId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

export function useOrgTopShadowAiUsers(
  organizationId: string | undefined,
  options: { limit?: number } = {},
  enabled: boolean = true
) {
  const { limit = 10 } = options;

  return useQuery({
    queryKey: queryKeys.shadowAi.topUsers(organizationId, limit),
    queryFn: () => {
      if (!organizationId) {
        throw new Error('organizationId is required');
      }
      return getOrgTopShadowAiUsers(apiClient, organizationId, { limit });
    },
    enabled: enabled && !!organizationId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

export function useOrgTopShadowAiTools(
  organizationId: string | undefined,
  options: { limit?: number; sourceType?: 'installed' | 'browser' } = {},
  enabled: boolean = true
) {
  const { limit = 10, sourceType } = options;

  return useQuery({
    queryKey: queryKeys.shadowAi.topTools(organizationId, limit, sourceType),
    queryFn: () => {
      if (!organizationId) {
        throw new Error('organizationId is required');
      }
      return getOrgTopShadowAiTools(apiClient, organizationId, {
        limit,
        sourceType,
      });
    },
    enabled: enabled && !!organizationId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}
